import styled from 'styled-components';

import shelflixLogoWhite from '@/assets/shelflix-logo-white.png';
import shelflixLogo from '@/assets/shelflix-logo.png';
import { useDeviceDetect } from '@/utils/useMediaQuery';
import Icon from '@shelflix/ui-web/components/Icon';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@shelflix/ui-web/components/ui/collapsible';
import {
  Sidebar,
  SidebarContent,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from '@shelflix/ui-web/components/ui/sidebar';
import { Popover, PopoverContent, PopoverTrigger } from '@shelflix/ui-web/components/ui/popover';
import { useState, useCallback, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { useDarkMode } from '@/hooks/useDarkMode';
import { RestrictedComponent } from '@/layout/RestrictedComponent';
import { trpc } from '@/trpcClient';
import { cn } from '@/utils/utils';
import { useMutation, useQuery } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Switch } from './ui/switch';
import { useMemo } from 'react';
import { useHasScopes } from '@/hooks/useHasScopes';
import { isImagePreloaded, markImageAsPreloaded } from '@/utils/imagePreloadCache';
import { Skeleton } from '@shelflix/ui-web/components/ui/skeleton';
import { useUserStore } from '@/store/userStore';
import { getTenantUrl } from '@/utils/tenantDetection';

interface AppSidebarProps {
  logo?: string;
}

export function AppSidebar({ logo }: AppSidebarProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { toggleSidebar } = useSidebar();
  const { isDarkMode, toggleDarkMode } = useDarkMode();

  // Fallback logo based on dark mode (only used when there's no custom logo or on error)
  const fallbackLogo = isDarkMode ? shelflixLogoWhite : shelflixLogo;

  // Check if we have a valid custom logo URL (non-empty string)
  const hasCustomLogo = Boolean(logo && logo.trim());

  // Determine if the logo is already preloaded (from main.tsx or previous loads)
  const isPreloaded = useMemo(() => {
    if (!hasCustomLogo) return true; // Fallback logos are bundled, always ready
    return isImagePreloaded(logo);
  }, [hasCustomLogo, logo]);

  // Track if image failed to load (use fallback on error)
  const [logoError, setLogoError] = useState(false);

  // Track if image has loaded during this component instance
  const [localLoaded, setLocalLoaded] = useState(isPreloaded);

  // Reset error state when logo URL changes (but keep loaded state to prevent flicker)
  useEffect(() => {
    setLogoError(false);
    // Only reset localLoaded if the new logo is not preloaded
    if (!isPreloaded) {
      // Don't reset to false immediately - check if it's a similar URL (same base path)
      // This prevents flicker when presigned URL query params change
      setLocalLoaded(isPreloaded);
    }
  }, [logo, isPreloaded]);

  // Calculate the effective logo URL
  const logoSrc = useMemo(() => {
    // If there's no custom logo or it errored, use fallback based on dark mode
    if (!hasCustomLogo || logoError) {
      return fallbackLogo;
    }
    return (logo ?? '').trim();
  }, [logo, hasCustomLogo, logoError, fallbackLogo]);

  // Handle logo load success
  const handleLogoLoad = useCallback(() => {
    setLocalLoaded(true);
    setLogoError(false);
    // Mark in global cache so other components know it's ready
    if (hasCustomLogo && logo) {
      markImageAsPreloaded(logo);
    }
  }, [logo, hasCustomLogo]);

  // Handle logo load error
  const handleLogoError = useCallback(
    (e: React.SyntheticEvent<HTMLImageElement>) => {
      setLogoError(true);
      setLocalLoaded(true); // Mark as loaded to show fallback
      e.currentTarget.src = fallbackLogo;
    },
    [fallbackLogo]
  );

  // Logo is considered loaded if it was preloaded or loaded locally
  const logoLoaded = isPreloaded || localLoaded;

  // Memoize powered by logo to prevent recalculation
  const poweredByLogoSrc = useMemo(() => {
    return isDarkMode ? shelflixLogoWhite : shelflixLogo;
  }, [isDarkMode]);

  const menuItems: { title: string; requiresScopes: string[]; icon: { name: string; fill?: 1 | 0 }; path: string }[] = [
    {
      title: 'Dashboard',
      requiresScopes: ['dashboard:view'],
      icon: { name: 'insert_chart', fill: 1 },
      path: '/dashboard',
    },
    {
      title: 'Materiais de PDV',
      requiresScopes: ['material:view'],
      icon: { name: 'connected_tv', fill: 0 },
      path: '/pos',
    },
    {
      title: 'Campanhas',
      requiresScopes: ['campaign:view'],
      icon: { name: 'hub', fill: 1 },
      path: '/campaign',
    },
    {
      title: 'Mídias',
      requiresScopes: ['media:view'],
      icon: { name: 'hangout_video', fill: 0 },
      path: '/media',
    },
    {
      title: 'Sensores de fluxo',
      requiresScopes: [], // sempre visível; a tela libera admin, master ou quem tem flow_sensor:view
      icon: { name: 'sensors', fill: 0 },
      path: '/flow-sensor',
    },
  ];

  const adminSubmenuItems: { title: string; path: string; requiresScopes: string[]; masterOnly?: boolean }[] = [
    { title: 'Equipe e Acessos', path: '/user', requiresScopes: ['admin:users:view'] },
    { title: 'Estabelecimentos', path: '/facility', requiresScopes: ['admin:facilities:view'] },
    { title: 'Marcas', path: '/brand', requiresScopes: ['admin:brands:view'] },
    { title: 'Catálogo de materiais', path: '/material', requiresScopes: ['admin:material_type:manage'] },
    {
      title: 'Notificações WhatsApp',
      path: '/whatsapp-notifications',
      requiresScopes: ['admin:notifications:manage'],
    },
    { title: 'Aparência', path: '/application-config', requiresScopes: ['admin:theme:manage'] },
    { title: 'Ações em lote', path: '/batch-action', requiresScopes: [], masterOnly: true },
  ];

  const isSelected = (path: string) => {
    return (path === '/' && location.pathname === path) || (path !== '/' && location.pathname.startsWith(path));
  };
  const { isMobile } = useDeviceDetect();

  // Check if user is a master user (can access all tenants)
  const user = useUserStore((state) => state.user);
  const isMaster = user?.isMaster ?? false;

  const visibleAdminSubmenuItems = adminSubmenuItems.filter((item) => !item.masterOnly || isMaster);
  const isAdminItemSelected = () => visibleAdminSubmenuItems.some((i) => location.pathname.startsWith(i.path));

  const [adminOpen, setAdminOpen] = useState(false);

  // Check if user has ANY admin permission by checking each one
  const hasUsersView = useHasScopes(['admin:users:view']);
  const hasFacilitiesView = useHasScopes(['admin:facilities:view']);
  const hasBrandsView = useHasScopes(['admin:brands:view']);
  const hasMaterialTypeManage = useHasScopes(['admin:material_type:manage']);
  const hasNotificationsManage = useHasScopes(['admin:notifications:manage']);
  const hasThemeManage = useHasScopes(['admin:theme:manage']);

  const hasAnyAdminPermission =
    hasUsersView ||
    hasFacilitiesView ||
    hasBrandsView ||
    hasMaterialTypeManage ||
    hasNotificationsManage ||
    hasThemeManage ||
    isMaster;

  // Fetch tenants list only for master users
  const { data: tenants } = useQuery({
    ...trpc.tenant.list.queryOptions(),
    enabled: isMaster,
  });

  // State for tenant switcher popover
  const [tenantPopoverOpen, setTenantPopoverOpen] = useState(false);

  const logoutMutation = useMutation(trpc.authentication.logout.mutationOptions());

  const logout = () => {
    const token = localStorage.getItem('authToken');
    logoutMutation.mutate(
      {
        token,
      },
      {
        onSuccess: () => {
          localStorage.removeItem('authToken');
          localStorage.removeItem('refreshToken');
          navigate('/login');
        },
        onError: (error) => {
          toast.error(`Erro ao sair do sistema: ${error.message}`);
        },
      }
    );
  };
  return (
    <Sidebar variant="floating" className={`fixed inset-y-0 left-0 z-30 ml-2`} containerClassName="sidebar-glass ">
      <div className="mt-2 flex items-center justify-center px-4 py-2">
        <div className="flex items-center gap-3 rounded-lg p-2 transition-all duration-300">
          <Switch
            checked={isDarkMode}
            onCheckedChange={toggleDarkMode}
            className="border-input border-2 data-[state=checked]:bg-[var(--background)] data-[state=unchecked]:bg-[var(--background)]"
          />
        </div>
      </div>
      <SidebarHeader className="flex flex-col items-center">
        <div className="flex flex-col items-center px-6 pb-2">
          {/* Fixed height container to prevent layout shift - taller to accommodate square logos */}
          <div className="relative flex h-[80px] w-full items-center justify-center">
            {!logoLoaded && <Skeleton className="absolute h-[60px] w-[120px] rounded" />}
            <img
              src={logoSrc}
              alt="Logo"
              className={cn(
                'max-h-[80px] max-w-full object-contain transition-opacity duration-200',
                logoLoaded ? 'opacity-100' : 'opacity-0'
              )}
              onLoad={handleLogoLoad}
              onError={handleLogoError}
            />
          </div>
          <div className="mt-2 flex items-center gap-1">
            <span className="text-[12px]">Powered by</span>
            <img className="h-[12px]" alt="Group" src={poweredByLogoSrc} />
          </div>
        </div>
      </SidebarHeader>

      <SidebarContent className="flex h-full flex-col p-1">
        <SidebarMenu className="flex flex-1 flex-col gap-4">
          {menuItems.map((item) => (
            <RestrictedComponent requiredScopes={item.requiresScopes} key={item.path}>
              <SidebarMenuItem key={item.path}>
                <SidebarMenuButton
                  className={cn(
                    `flex cursor-pointer items-center gap-4 border-none p-6 px-4 shadow-none transition-colors`,
                    isSelected(item.path)
                      ? 'bg-[var(--primary)]/30 text-[var(--primary)]'
                      : 'hover:bg-[var(--primary)]/30',
                    'hover:bg-[var(--primary)]/30 active:bg-[var(--primary)]/30 active:font-medium active:text-[var(--primary)]'
                  )}
                  onClick={() => {
                    if (isMobile) toggleSidebar();
                    navigate(item.path);
                  }}
                >
                  <Icon
                    size={28}
                    name={item.icon.name}
                    fill={item.icon.fill}
                    className={isSelected(item.path) ? 'text-primary' : 'text-[var(--sidebar-foreground)]'}
                  />
                  <span
                    className={
                      isSelected(item.path)
                        ? 'text-primary text-base font-medium'
                        : 'text-base text-[var(--sidebar-foreground)]'
                    }
                  >
                    {item.title}
                  </span>
                </SidebarMenuButton>
              </SidebarMenuItem>
            </RestrictedComponent>
          ))}

          {hasAnyAdminPermission && (
            <CollapsibleStyled
              defaultOpen={isAdminItemSelected()}
              className={`${isAdminItemSelected() && 'bg-primary/30'}`}
              onOpenChange={setAdminOpen}
            >
              <CollapsibleTriggerStyled className="flex h-[50px] w-full items-center gap-4 rounded-[10px] px-4 hover:bg-[var(--primary)]/30">
                <Icon
                  size={28}
                  name="admin_panel_settings"
                  fill={1}
                  className={isAdminItemSelected() ? 'text-primary' : 'text-[var(--sidebar-foreground)]'}
                />
                <section className="flex cursor-pointer items-center gap-1">
                  <span
                    className={`${
                      isAdminItemSelected() ? 'text-primary' : 'text-[var(--sidebar-foreground)]'
                    } flex-1 text-base font-medium`}
                  >
                    Administrador
                  </span>
                  <Icon
                    name={isAdminItemSelected() || adminOpen ? 'arrow_drop_down' : 'arrow_right'}
                    size={28}
                    className={isAdminItemSelected() ? 'text-primary' : 'text-[var(--sidebar-foreground)]'}
                  />
                </section>
              </CollapsibleTriggerStyled>

              <CollapsibleContent className="mt-2 space-y-1">
                {visibleAdminSubmenuItems.map((item) => (
                  <RestrictedComponent requiredScopes={item.requiresScopes} key={item.path}>
                    <div
                      key={item.path}
                      className={cn(
                        `flex h-[50px] cursor-pointer items-center rounded-md px-6 transition-colors hover:bg-[var(--primary)]/50`,

                        isSelected(item.path)
                          ? 'bg-[var(--primary)]/30 text-[var(--primary)]'
                          : 'hover:bg-[var(--primary)]/30',
                        'hover:bg-[var(--primary)]/30'
                      )}
                      onClick={() => navigate(item.path)}
                    >
                      <div
                        className={`min-h-2 min-w-2 rounded-md ${isSelected(item.path) ? 'bg-primary' : 'bg-[var(--sidebar-foreground)]'}`}
                      />
                      <span
                        className={`hover:text-[var(--sidebar-foreground) ml-6 font-['Poppins'] text-sm ${
                          isSelected(item.path)
                            ? 'text-primary font-medium'
                            : 'font-normal text-[var(--sidebar-foreground)]'
                        } `}
                      >
                        {item.title}
                      </span>
                    </div>
                  </RestrictedComponent>
                ))}
              </CollapsibleContent>
            </CollapsibleStyled>
          )}

          <SidebarMenuItem className="mt-auto">
            <div className="flex w-full items-center justify-between">
              <SidebarMenuButton
                className={`flex h-[67px] flex-1 cursor-pointer items-center gap-4 border-none px-4 shadow-none transition-colors hover:bg-transparent`}
                onClick={() => {
                  logout();
                }}
              >
                <Icon name={'logout'} size={28} fill={1} className={'rotate-180 text-[var(--sidebar-foreground)]'} />
                <span className={'text-base text-[var(--sidebar-foreground)]'}>Sair</span>
              </SidebarMenuButton>
              {isMaster && (
                <Popover open={tenantPopoverOpen} onOpenChange={setTenantPopoverOpen}>
                  <PopoverTrigger asChild>
                    <SidebarMenuButton
                      className={`mr-2 flex h-[50px] w-[50px] cursor-pointer items-center justify-center rounded-[10px] border-none shadow-none transition-colors hover:bg-[var(--primary)]/30`}
                    >
                      <Icon name={'apartment'} size={28} fill={1} className={'text-[var(--sidebar-foreground)]'} />
                    </SidebarMenuButton>
                  </PopoverTrigger>
                  <PopoverContent side="top" align="end" className="max-h-[300px] w-[200px] overflow-y-auto p-2">
                    <div className="flex flex-col gap-1">
                      <span className="text-muted-foreground mb-2 px-2 text-xs font-medium">Clientes</span>
                      {tenants?.map((tenant) => (
                        <button
                          key={tenant.uuid}
                          className="flex w-full cursor-pointer items-center gap-2 rounded-md px-2 py-2 text-left text-sm transition-colors hover:bg-[var(--primary)]/30"
                          onClick={() => {
                            const url = getTenantUrl(tenant);
                            window.open(url, '_blank');
                            setTenantPopoverOpen(false);
                          }}
                        >
                          <Icon name={'apartment'} size={16} fill={0} className={'text-[var(--sidebar-foreground)]'} />
                          <span className="truncate">{tenant.name}</span>
                        </button>
                      ))}
                      {(!tenants || tenants.length === 0) && (
                        <span className="text-muted-foreground px-2 py-2 text-sm">Nenhum tenant encontrado</span>
                      )}
                    </div>
                  </PopoverContent>
                </Popover>
              )}
            </div>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarContent>
    </Sidebar>
  );
}

const CollapsibleStyled = styled(Collapsible)`
  border-radius: 0.5rem;
`;

const CollapsibleTriggerStyled = styled(CollapsibleTrigger)`
  &[data-state='open'] {
    hover {
      opacity: 0.2;
    }
  }
`;
